<template>
    <td
        :class="[
            'table-cell',
            fitToContent && 'fit-to-content',
            to && 'cell-clickable',
            disableTextWrap && 'disable-text-wrap',
            textAlignClass,
            verticalAlignClass,
        ]"
        :style="styleObject"
        @click="handleNavigation"
    >
        <slot />
    </td>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import { useRouter } from "vue-router";
import type { Align, VerticalAlign } from "@/types/alignment";

export default defineComponent({
    name: "TableCell",
    props: {
        // Shrinks the column to its content and keeps it out of the share of the spare width, so
        // the other columns take all of it. Meant for content of a fixed size: actions, icons,
        // switches, the labels of a label/value table. A column of data needs nothing: the table
        // gives each column the room of its longest value and splits the rest in proportion
        fitToContent: {
            type: Boolean,
            default: false,
        },
        to: {
            type: String,
            default: "",
        },
        align: {
            type: String as PropType<Align>,
            default: "left",
        },
        verticalAlign: {
            type: String as PropType<VerticalAlign>,
            default: "middle",
        },
        minWidth: {
            type: Number,
            default: 0,
        },
        // Keeps the value on one line, for a value that must not break, such as a date. Unlike
        // fitToContent, the column still takes its share of the spare width
        disableTextWrap: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const router = useRouter();

        const handleNavigation = () => {
            if (props.to) {
                router.push(props.to);
            }
        };

        const textAlignClass = computed(() => {
            return (
                {
                    left: "text-left",
                    center: "text-center",
                    right: "text-right",
                }[props.align] || ""
            );
        });

        const verticalAlignClass = computed(() => {
            return (
                {
                    top: "align-top",
                    middle: "align-middle",
                    bottom: "align-bottom",
                }[props.verticalAlign] || ""
            );
        });

        return {
            handleNavigation,
            textAlignClass,
            verticalAlignClass,
        };
    },
    computed: {
        styleObject(): Record<string, string> {
            return this.minWidth > 0 ? { minWidth: `${this.minWidth}px` } : {};
        },
    },
});
</script>

<style>
@import "@/style/data-tables.css";
</style>
